import { useEffect, useMemo, useState } from "react";
import Layout from "../components/Layout";
import KYCCard from "../components/KYCCard";
import { api } from "../lib/api";
import {
  Banknote, Wallet as WalletIcon, DollarSign, Trash2,
  CheckCircle2, Clock, XCircle, Plus, Building2, AtSign, Bitcoin, CreditCard,
} from "lucide-react";
import { toast } from "sonner";
import { validateCryptoAddress, detectChainFromAddress } from "../lib/cryptoAddress";
import { useAuth } from "../context/AuthContext";
import { Mail } from "lucide-react";

const TYPE_META = {
  bank:    { icon: Building2, gradient: "from-emerald-400 to-emerald-600" },
  venmo:   { icon: AtSign,    gradient: "from-sky-400 to-sky-600" },
  paypal:  { icon: WalletIcon,gradient: "from-blue-500 to-indigo-600" },
  cashapp: { icon: DollarSign,gradient: "from-green-400 to-emerald-500" },
  chime:   { icon: WalletIcon,gradient: "from-lime-400 to-green-500" },
  crypto:  { icon: Bitcoin,   gradient: "from-amber-400 to-orange-600" },
  stripe:  { icon: CreditCard,gradient: "from-violet-500 to-fuchsia-600" },
};

const STATUS_PILL = {
  pending:   { icon: Clock,        color: "text-primary border-primary/40 bg-primary/10",               label: "Pending" },
  screening: { icon: Clock,        color: "text-primary border-primary/40 bg-primary/10",               label: "Screening" },
  sending:   { icon: Clock,        color: "text-primary border-primary/40 bg-primary/10",               label: "Sending" },
  paid:      { icon: CheckCircle2, color: "text-secondary border-secondary/40 bg-secondary/10",         label: "Paid" },
  failed:    { icon: XCircle,      color: "text-destructive border-destructive/40 bg-destructive/10",   label: "Failed" },
  rejected:  { icon: XCircle,      color: "text-destructive border-destructive/40 bg-destructive/10",   label: "Rejected" },
};

export default function Cashout() {
  const { user } = useAuth();
  const emailUnverified = user && user.emailVerified !== true;
  const [methods, setMethods] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [wallet, setWallet] = useState({ balance: 0 });
  const [pricing, setPricing] = useState({ usdPerCaul: 0.01, minWithdrawCaul: 1000, minWithdrawUsd: 10, supportedTypes: [] });
  const [lockups, setLockups] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ type: "bank", handle: "", label: "", chain: "base" });
  const [withdrawForm, setWithdrawForm] = useState({ methodId: "", amountCaul: "" });

  const load = async () => {
    const [m, w, wal, pr, lk] = await Promise.all([
      api.get("/payouts/methods"),
      api.get("/payouts/withdrawals"),
      api.get("/wallet"),
      api.get("/payouts/pricing"),
      api.get("/rewards/lockups").catch(() => ({ data: null })),
    ]);
    setMethods(m.data);
    setWithdrawals(w.data);
    setWallet(wal.data.wallet || { balance: 0 });
    setPricing(pr.data);
    setLockups(lk.data);
    if (m.data.length > 0 && !withdrawForm.methodId) {
      const def = m.data.find((x) => x.isDefault) || m.data[0];
      setWithdrawForm((f) => ({ ...f, methodId: def.id }));
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const usdEquiv = useMemo(() => Math.max(0, Math.round((parseInt(withdrawForm.amountCaul || 0) || 0) * pricing.usdPerCaul * 100) / 100), [withdrawForm.amountCaul, pricing.usdPerCaul]);

  const addMethod = async () => {
    if (!form.handle.trim()) { toast.error("Please enter your account details"); return; }
    if (form.type === "crypto") {
      const v = validateCryptoAddress(form.handle, form.chain);
      if (!v.ok && v.code !== "empty") {
        toast.error(v.message);
        return;
      }
    }
    try {
      await api.post("/payouts/methods", form);
      toast.success("Payout method added");
      setShowAdd(false);
      setForm({ type: "bank", handle: "", label: "", chain: "base" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this payout method?")) return;
    try { await api.delete(`/payouts/methods/${id}`); load(); toast.success("Removed"); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const setDefault = async (id) => {
    try { await api.post(`/payouts/methods/${id}/default`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const submitWithdraw = async () => {
    const amount = parseInt(withdrawForm.amountCaul);
    if (!amount || amount < pricing.minWithdrawCaul) {
      toast.error(`Minimum withdrawal is ${pricing.minWithdrawCaul} CAUL ($${pricing.minWithdrawUsd})`);
      return;
    }
    if (amount > wallet.balance) {
      toast.error(`You only have ${wallet.balance} CAUL`);
      return;
    }
    if (!withdrawForm.methodId) {
      toast.error("Please add a payout method first");
      return;
    }
    try {
      await api.post("/payouts/withdraw", { methodId: withdrawForm.methodId, amountCaul: amount });
      toast.success(`Cashout requested · $${(amount * pricing.usdPerCaul).toFixed(2)} pending`);
      setWithdrawForm((f) => ({ ...f, amountCaul: "" }));
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <Layout>
      <div className="space-y-8">
        <div>
          <div className="label-mystic">Cash Out</div>
          <h1 className="font-serif text-5xl mt-2">CAUL → USD</h1>
          <p className="text-foreground/70 mt-2 text-sm max-w-2xl">
            Convert your Cauldron Reward Token to real US dollars. Add a payout method below — bank, Venmo, PayPal, Cash App, Chime, or a crypto wallet — then request a withdrawal anytime.
          </p>
        </div>

        {emailUnverified && (
          <div
            className="parchment p-4 border-secondary/40 bg-secondary/[0.06] flex items-start gap-3"
            data-testid="cashout-email-unverified-card"
          >
            <Mail className="w-5 h-5 text-secondary mt-0.5 flex-shrink-0" />
            <div className="text-sm leading-relaxed">
              <div className="font-medium text-secondary mb-0.5">Verify your email to cash out</div>
              <div className="text-foreground/75">
                For fraud protection, withdrawals require a verified email. Use the
                <strong className="text-foreground"> Verify your email </strong>
                banner at the top of the page to claim your account — takes 30 seconds.
              </div>
            </div>
          </div>
        )}

        <KYCCard onApproved={load} />

        {/* ── Lockup banner (if user has any bonus lockups) ── */}
        {lockups && lockups.totalLocked > 0 && (
          <div className="parchment p-4 border-primary/30 bg-primary/[0.04]" data-testid="cashout-lockup-banner">
            <div className="flex items-start gap-3 flex-wrap">
              <div className="flex-1 min-w-[240px]">
                <div className="label-mystic text-primary">CAUL Tokenomics · Bonus Lockups</div>
                <div className="font-serif text-2xl mt-1">{lockups.stillLocked} <span className="text-foreground/50 text-base">/ {lockups.totalLocked} CAUL locked</span></div>
                <p className="text-xs text-foreground/65 mt-2 leading-relaxed max-w-2xl">
                  Bonus CAUL ({lockups.lockups?.length} grant{lockups.lockups?.length === 1 ? "" : "s"}) unlocks in 100-CAUL drips on each paid transaction.
                  Cashout of bonus tokens opens {lockups.exportLockDays} days after grant ({lockups.pendingExport} CAUL unlocked &amp; awaiting that window).
                  Cashout cap: <b>{Math.round(lockups.cashoutPctCap * 100)}%</b> of your CAUL balance per <b>{lockups.cashoutWindowHours}h</b>.
                </p>
              </div>
              <div className="text-right space-y-1">
                <div>
                  <div className="label-mystic">Available now</div>
                  <div className="font-serif text-2xl text-secondary" data-testid="cashout-remaining-now">{lockups.remainingNow ?? 0} <span className="text-foreground/40 text-base">CAUL</span></div>
                </div>
                <div className="text-[10px] text-foreground/50 uppercase tracking-wider">
                  {lockups.spent48h > 0 ? `${lockups.spent48h} CAUL used in last ${lockups.cashoutWindowHours}h` : `Cap resets every ${lockups.cashoutWindowHours}h`}
                </div>
                {lockups.nextResetAt && (
                  <div className="text-[10px] text-foreground/50">
                    Next refresh · {new Date(lockups.nextResetAt).toLocaleString()}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ── Top stat row ── */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="parchment p-6">
            <div className="label-mystic">CAUL Balance</div>
            <div className="font-serif text-5xl text-secondary mt-2" data-testid="cashout-balance">{wallet.balance}</div>
            <div className="text-xs text-foreground/60 mt-1">≈ ${(wallet.balance * pricing.usdPerCaul).toFixed(2)} USD</div>
          </div>
          <div className="parchment p-6">
            <div className="label-mystic">Min Withdrawal</div>
            <div className="font-serif text-5xl text-secondary mt-2">${pricing.minWithdrawUsd}</div>
            <div className="text-xs text-foreground/60 mt-1">= {pricing.minWithdrawCaul} CAUL</div>
          </div>
          <div className="parchment p-6">
            <div className="label-mystic">Exchange Rate</div>
            <div className="font-serif text-5xl text-secondary mt-2">${pricing.usdPerCaul.toFixed(3)}</div>
            <div className="text-xs text-foreground/60 mt-1">per 1 CAUL</div>
          </div>
        </div>

        {/* ── Payout methods ── */}
        <section>
          <div className="flex items-center justify-between mb-3">
            <div className="label-mystic">Payout Methods</div>
            <button onClick={() => setShowAdd(!showAdd)} className="text-xs uppercase tracking-wider text-secondary hover:text-secondary/80 flex items-center gap-1" data-testid="cashout-add-method-toggle">
              <Plus size={14} strokeWidth={1.5} /> {showAdd ? "Cancel" : "Add Method"}
            </button>
          </div>

          {showAdd && (
            <div className="parchment p-6 space-y-3 mb-3" data-testid="cashout-add-method-form">
              <div>
                <label className="label-mystic mb-2 block">Payout Type</label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {pricing.supportedTypes.map((t) => {
                    const meta = TYPE_META[t.id] || {};
                    const Icon = meta.icon || Banknote;
                    return (
                      <button key={t.id} type="button" onClick={() => setForm({ ...form, type: t.id })}
                        className={`p-3 border rounded-sm text-left flex items-center gap-2 ${form.type === t.id ? "border-secondary bg-secondary/10" : "border-secondary/20 hover:bg-secondary/5"}`}
                        data-testid={`cashout-type-${t.id}`}>
                        <Icon size={16} strokeWidth={1.5} className="text-secondary" />
                        <span className="text-sm">{t.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })}
                placeholder="Nickname (e.g., 'Chase Checking')" className="w-full bg-input border border-secondary/20 p-3 rounded-sm" data-testid="cashout-label" />

              {form.type === "bank" ? (
                <div className="grid grid-cols-1 gap-3">
                  <input placeholder="Routing number"
                    onChange={(e) => setForm({ ...form, _routing: e.target.value, handle: `${e.target.value}|${form._account || ""}|${form._name || ""}` })}
                    className="w-full bg-input border border-secondary/20 p-3 rounded-sm" data-testid="cashout-bank-routing" />
                  <input placeholder="Account number"
                    onChange={(e) => setForm({ ...form, _account: e.target.value, handle: `${form._routing || ""}|${e.target.value}|${form._name || ""}` })}
                    className="w-full bg-input border border-secondary/20 p-3 rounded-sm" data-testid="cashout-bank-account" />
                  <input placeholder="Account holder name"
                    onChange={(e) => setForm({ ...form, _name: e.target.value, handle: `${form._routing || ""}|${form._account || ""}|${e.target.value}` })}
                    className="w-full bg-input border border-secondary/20 p-3 rounded-sm" data-testid="cashout-bank-name" />
                </div>
              ) : form.type === "crypto" ? (
                (() => {
                  const addrCheck = validateCryptoAddress(form.handle, form.chain);
                  const onAddrChange = (raw) => {
                    // Auto-detect chain so users don't pick the wrong network.
                    // XRPL paste keeps the current chain (the warning takes
                    // over and tells them to swap rails). EVM paste flips to
                    // Base if currently on Solana. Solana paste flips to solana.
                    const next = { ...form, handle: raw };
                    const { chain: detected } = detectChainFromAddress(raw);
                    const evmChains = ["base", "ethereum", "polygon", "arbitrum", "optimism"];
                    if (detected === "evm" && !evmChains.includes(form.chain)) {
                      next.chain = "base";
                    } else if (detected === "solana" && form.chain !== "solana") {
                      next.chain = "solana";
                    }
                    setForm(next);
                  };
                  return (
                <div className="grid grid-cols-1 gap-3">
                  <select value={form.chain} onChange={(e) => setForm({ ...form, chain: e.target.value })}
                    className="w-full bg-input border border-secondary/20 p-3 rounded-sm" data-testid="cashout-crypto-chain">
                    <option value="base">USDC on Base (recommended — lowest gas)</option>
                    <option value="ethereum">USDC on Ethereum (higher gas)</option>
                    <option value="polygon">USDC on Polygon</option>
                    <option value="arbitrum">USDC on Arbitrum</option>
                    <option value="optimism">USDC on Optimism</option>
                    <option value="solana">USDC on Solana</option>
                  </select>
                  <input value={form.handle} onChange={(e) => onAddrChange(e.target.value)}
                    placeholder="Wallet address (0x… or Solana base58)"
                    className={`w-full bg-input border p-3 rounded-sm font-mono text-xs ${
                      !addrCheck.ok && addrCheck.code !== "empty"
                        ? "border-destructive/60 focus:border-destructive"
                        : "border-secondary/20"
                    }`}
                    data-testid="cashout-crypto-address"
                    aria-invalid={!addrCheck.ok && addrCheck.code !== "empty"}
                  />
                  {addrCheck.ok && form.handle && (
                    <div className="text-[11px] text-secondary/85 -mt-1" data-testid="cashout-crypto-chain-detected">
                      Detected chain: <strong className="uppercase tracking-wider">{form.chain}</strong> ✓
                    </div>
                  )}
                  {!addrCheck.ok && addrCheck.code === "xrpl" && (
                    <div
                      className="text-xs leading-relaxed bg-destructive/10 border border-destructive/40 text-destructive p-3 rounded-sm"
                      data-testid="cashout-crypto-xrpl-warning"
                    >
                      <strong className="block mb-1">⚠️ XRPL address detected</strong>
                      That looks like your CAUL wallet on XRPL. Stripe Bridge can't deliver USDC to XRPL —
                      please paste a <strong>Base / Ethereum / Polygon</strong> address (starts with <code className="font-mono">0x…</code>) or a <strong>Solana</strong> address instead.
                    </div>
                  )}
                  {!addrCheck.ok && addrCheck.code === "format" && (
                    <div
                      className="text-xs leading-relaxed bg-primary/10 border border-primary/40 text-primary p-3 rounded-sm"
                      data-testid="cashout-crypto-format-warning"
                    >
                      {addrCheck.message}
                    </div>
                  )}
                  <p className="text-[11px] text-foreground/55 leading-relaxed">
                    Identity verification required. We send USDC via Stripe Bridge to your wallet — typically arrives within 5 minutes. Up to $500/mo per the Basic verification tier.
                  </p>
                </div>
                  );
                })()
              ) : form.type === "stripe" ? (
                <div className="space-y-3">
                  <div className="text-sm text-foreground/75 bg-secondary/5 border border-secondary/20 p-3 rounded-sm">
                    Already onboarded with Stripe Connect on your provider profile?
                    <button
                      type="button"
                      onClick={async () => {
                        try {
                          await api.post("/payouts/methods/stripe/sync");
                          toast.success("Stripe Connect linked");
                          setShowAdd(false);
                          load();
                        } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
                      }}
                      className="ml-2 text-secondary hover:text-secondary/80 underline text-xs uppercase tracking-wider"
                      data-testid="cashout-stripe-sync"
                    >
                      Sync now
                    </button>
                  </div>
                  <input value={form.handle} onChange={(e) => setForm({ ...form, handle: e.target.value })}
                    placeholder="Stripe Connect account ID (acct_…) or email"
                    className="w-full bg-input border border-secondary/20 p-3 rounded-sm font-mono text-xs"
                    data-testid="cashout-stripe-handle" />
                </div>
              ) : (
                <input value={form.handle} onChange={(e) => setForm({ ...form, handle: e.target.value })}
                  placeholder={
                    form.type === "venmo"   ? "@your-venmo-handle"
                  : form.type === "paypal"  ? "you@example.com"
                  : form.type === "cashapp" ? "$YourCashtag"
                  : form.type === "chime"   ? "$YourChimeSign"
                  : "handle"
                  }
                  className="w-full bg-input border border-secondary/20 p-3 rounded-sm" data-testid="cashout-handle" />
              )}

              <button onClick={addMethod} className="bg-primary text-primary-foreground px-6 py-2 rounded-sm uppercase tracking-wider text-xs" data-testid="cashout-add-submit">Add Method</button>
            </div>
          )}

          {methods.length === 0 ? (
            <div className="parchment p-8 text-center text-foreground/50 italic font-serif text-xl">No payout methods yet. Add one to start cashing out.</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="cashout-methods-list">
              {methods.map((m) => {
                const meta = TYPE_META[m.type] || {};
                const Icon = meta.icon || Banknote;
                return (
                  <div key={m.id} className="parchment p-4 flex items-center gap-3" data-testid={`cashout-method-${m.id}`}>
                    <div className={`w-12 h-12 rounded-sm bg-gradient-to-br ${meta.gradient || "from-secondary to-primary"} flex items-center justify-center shrink-0`}>
                      <Icon size={20} strokeWidth={1.5} className="text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-serif">{m.label || m.typeLabel}</div>
                      <div className="text-xs text-foreground/60 font-mono">{m.maskedHandle}{m.chain ? ` · ${m.chain}` : ""}</div>
                      {m.isDefault && <span className="label-mystic text-secondary text-[10px]">Default</span>}
                    </div>
                    <div className="flex flex-col gap-1">
                      {!m.isDefault && (
                        <button onClick={() => setDefault(m.id)} className="text-[10px] uppercase tracking-wider text-secondary hover:text-secondary/80" data-testid={`cashout-default-${m.id}`}>
                          Set default
                        </button>
                      )}
                      <button onClick={() => remove(m.id)} className="text-[10px] uppercase tracking-wider text-destructive/80 hover:text-destructive flex items-center gap-1 self-end" data-testid={`cashout-remove-${m.id}`}>
                        <Trash2 size={11} strokeWidth={1.5} /> Remove
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* ── Request withdrawal ── */}
        <section>
          <div className="label-mystic mb-3">Request Withdrawal</div>
          <div className="parchment p-6 space-y-3" data-testid="cashout-withdraw-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="label-mystic mb-1 block">Send to</label>
                <select value={withdrawForm.methodId} onChange={(e) => setWithdrawForm({ ...withdrawForm, methodId: e.target.value })}
                  className="w-full bg-input border border-secondary/20 p-3 rounded-sm" data-testid="cashout-withdraw-method">
                  <option value="">Select payout method</option>
                  {methods.map((m) => (
                    <option key={m.id} value={m.id}>{m.label || m.typeLabel} · {m.maskedHandle}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label-mystic mb-1 block">Amount (CAUL)</label>
                <input type="number" value={withdrawForm.amountCaul} onChange={(e) => setWithdrawForm({ ...withdrawForm, amountCaul: e.target.value })}
                  placeholder={`min ${pricing.minWithdrawCaul}`} className="w-full bg-input border border-secondary/20 p-3 rounded-sm" data-testid="cashout-withdraw-amount" />
              </div>
            </div>
            <div className="text-sm text-foreground/70">
              You'll receive <span className="text-secondary font-serif text-xl">${usdEquiv.toFixed(2)} USD</span>. Processed within 1–3 business days.
            </div>
            {lockups && lockups.remainingNow != null && parseInt(withdrawForm.amountCaul || 0) > lockups.remainingNow && (
              <div className="text-xs text-destructive/90" data-testid="cashout-cap-warn">
                Exceeds your {Math.round(lockups.cashoutPctCap * 100)}% / {lockups.cashoutWindowHours}h cap.
                Max allowed right now: <b>{lockups.remainingNow} CAUL</b>{" "}
                (≈ ${(lockups.remainingNow * pricing.usdPerCaul).toFixed(2)}).
              </div>
            )}
            <button onClick={submitWithdraw} disabled={methods.length === 0} className="w-full bg-primary text-primary-foreground px-6 py-3 rounded-sm uppercase tracking-wider text-sm disabled:opacity-50" data-testid="cashout-withdraw-submit">
              Cash Out
            </button>
          </div>
        </section>

        {/* ── History ── */}
        <section>
          <div className="label-mystic mb-3">Withdrawal History</div>
          {withdrawals.length === 0 ? (
            <div className="parchment p-8 text-center text-foreground/50 italic">No withdrawals yet.</div>
          ) : (
            <div className="parchment divide-y divide-secondary/15" data-testid="cashout-history">
              {withdrawals.map((w) => {
                const meta = STATUS_PILL[w.status] || STATUS_PILL.pending;
                const Icon = meta.icon;
                return (
                  <div key={w.id} className="p-4 flex items-center gap-3 flex-wrap" data-testid={`withdrawal-${w.id}`}>
                    <div className="flex-1 min-w-[180px]">
                      <div className="font-serif text-lg">${w.usdAmount.toFixed(2)} <span className="text-foreground/50 text-sm">· {w.amountCaul} CAUL</span></div>
                      <div className="text-xs text-foreground/60">→ {w.methodLabel || w.methodType} · {w.maskedHandle}{w.chain ? ` · ${w.chain}` : ""}</div>
                      <div className="label-mystic mt-1">{new Date(w.createdAt).toLocaleString()}</div>
                      {w.reason && <div className="text-xs text-destructive/80 italic mt-1">{w.reason}</div>}
                      {w.failureReason && <div className="text-xs text-destructive/80 italic mt-1">{w.failureReason}</div>}
                      {w.blockExplorerUrl && (
                        <a href={w.blockExplorerUrl} target="_blank" rel="noopener noreferrer"
                           className="text-xs text-secondary hover:text-secondary/80 mt-1 inline-block underline underline-offset-2"
                           data-testid={`withdrawal-explorer-${w.id}`}>
                          View on-chain →
                        </a>
                      )}
                    </div>
                    <span className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 border rounded-sm uppercase tracking-wider ${meta.color}`} data-testid={`withdrawal-status-${w.id}`}>
                      <Icon size={12} strokeWidth={1.75} /> {meta.label}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </div>
    </Layout>
  );
}
